import { CommonModule } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { PageDataPanelComponent } from 'src/app/shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from 'src/app/shared/page-layout/page-hero.component';
import { PageMetric } from 'src/app/shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from 'src/app/shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from 'src/app/shared/page-layout/page-shell.component';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { CrmClient } from 'src/app/crm/crm.model';
import { CrmService } from 'src/app/crm/crm.service';
import { WarehousingMasterData } from 'src/app/warehousing/warehousing.model';
import { WarehousingService } from 'src/app/warehousing/warehousing.service';
import { ContainerFacilityService } from '../container-facility.service';
import { ContainerTerminalYardService } from '../container-terminal-yard.service';
import { ContainerFacility } from '../models/container-facility.model';
import { ContainerYardArea, ContainerYardChargeRule, ContainerYardSlot } from '../models/container-terminal-yard.model';
import { ProductsService } from 'src/app/order/product.service';
import { Products } from 'src/app/order/models/product.model';

type SetupMaster = 'facilities' | 'areas' | 'slots' | 'charge-rules' | 'execution-values';

interface SetupCatalogItem {
  code: string;
  label: string;
  description: string;
  category?: string;
}

@Component({
  selector: 'app-yard-setup-master-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    ButtonModule,
    IftaLabelModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TagModule,
    TooltipModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent
  ],
  templateUrl: './yard-setup-master-list.component.html',
  styleUrl: './yard-setup-master.component.css'
})
export class YardSetupMasterListComponent implements OnInit {
  master: SetupMaster = 'facilities';
  isLoading = false;
  keyword = '';
  facilityFilter: number | null = null;
  activeFilter: 'ALL' | 'ACTIVE' | 'INACTIVE' = 'ALL';

  facilities: ContainerFacility[] = [];
  areas: ContainerYardArea[] = [];
  slots: ContainerYardSlot[] = [];
  chargeRules: ContainerYardChargeRule[] = [];
  warehouses: WarehousingMasterData[] = [];
  supplierClients: CrmClient[] = [];
  logisticsProducts: Products[] = [];

  readonly activeOptions = [
    { label: 'All', value: 'ALL' },
    { label: 'Active', value: 'ACTIVE' },
    { label: 'Inactive', value: 'INACTIVE' }
  ];

  readonly transportModes: SetupCatalogItem[] = [
    { code: 'TRUCK', label: 'Truck leg', description: 'Road movement between pickup, yard, warehouse, depot, or customer site.', category: 'Execution' },
    { code: 'YARD_TRANSFER', label: 'Yard transfer', description: 'Container transfer between yards, depots, or staging locations.', category: 'Execution' },
    { code: 'CUSTOMER_DELIVERY', label: 'Customer delivery', description: 'Delivery leg from yard or warehouse staging to the consignee/customer.', category: 'Execution' },
    { code: 'EMPTY_RETURN', label: 'Empty return', description: 'Return movement to the nominated empty depot or yard.', category: 'Closure' }
  ];

  readonly storageFacilityTypes: SetupCatalogItem[] = [
    { code: 'PORT_TERMINAL', label: 'Port terminal', description: 'Marine terminal for laden import/export container yard operations.', category: 'Terminal' },
    { code: 'BONDED_YARD', label: 'Bonded yard', description: 'Customs-controlled yard or bonded holding area.', category: 'Storage' },
    { code: 'CFS', label: 'CFS / transit shed', description: 'Container freight station bridging yard and warehouse operations.', category: 'Storage' },
    { code: 'WAREHOUSE', label: 'Warehouse staging', description: 'Full-container dwell at warehouse or CFS before destuffing.', category: 'Storage' },
    { code: 'EMPTY_DEPOT', label: 'Empty depot', description: 'Depot used for empty return completion.', category: 'Return' },
    { code: 'CARRIER_YARD', label: 'Carrier yard', description: 'Carrier-nominated yard for empty or laden handoff.', category: 'Storage' },
    { code: 'THIRD_PARTY_YARD', label: 'Third-party yard', description: 'External storage yard used by operations.', category: 'Storage' }
  ];

  readonly areaTypes: SetupCatalogItem[] = [
    { code: 'IMPORT_STACK', label: 'Import Stack', description: 'Inbound laden container stack.', category: 'Area' },
    { code: 'EXPORT_STACK', label: 'Export Stack', description: 'Outbound laden container stack.', category: 'Area' },
    { code: 'INSPECTION', label: 'Inspection', description: 'Customs, survey, or operations inspection area.', category: 'Area' },
    { code: 'REEFER', label: 'Reefer', description: 'Plugged reefer container area.', category: 'Area' },
    { code: 'EMPTY', label: 'Empty', description: 'Empty container holding area.', category: 'Area' },
    { code: 'DAMAGED', label: 'Damaged', description: 'Damaged container staging area.', category: 'Area' },
    { code: 'CUSTOMS_HOLD', label: 'Customs Hold', description: 'Hold area for customs-controlled units.', category: 'Area' }
  ];

  readonly legStatuses: SetupCatalogItem[] = [
    { code: 'PLANNED', label: 'Planned', description: 'Leg or storage period is scheduled but not active.', category: 'Status' },
    { code: 'ACTIVE', label: 'Active', description: 'Current execution or dwell record.', category: 'Status' },
    { code: 'COMPLETED', label: 'Completed', description: 'Execution is finished and contributes to closure.', category: 'Status' },
    { code: 'CANCELLED', label: 'Cancelled', description: 'Leg or dwell was cancelled and should not drive execution.', category: 'Status' },
    { code: 'EXCEPTION', label: 'Exception', description: 'Operational issue requires resolution or override.', category: 'Status' }
  ];

  readonly lifecycleMilestones: SetupCatalogItem[] = [
    { code: 'CASE_OPENED', label: 'Case opened', description: 'Lifecycle record is created.', category: 'System' },
    { code: 'STORAGE_RECEIVED', label: 'Received at yard', description: 'Container arrived into a yard/depot/storage leg.', category: 'Storage' },
    { code: 'STORAGE_SLOT_ASSIGNED', label: 'Slot assigned', description: 'A storage slot or bay is assigned.', category: 'Storage' },
    { code: 'YARD_GATE_IN', label: 'Yard gate in', description: 'Container entered the terminal yard.', category: 'Yard' },
    { code: 'YARD_GATE_OUT', label: 'Yard gate out', description: 'Container exited the terminal yard.', category: 'Yard' },
    { code: 'EMPTY_RETURNED', label: 'Empty returned', description: 'Return completion before normal case closure.', category: 'Closure' }
  ];

  constructor(
    private route: ActivatedRoute,
    private sessionStorageService: SessionStorageService,
    private crmService: CrmService,
    private warehousingService: WarehousingService,
    private facilityService: ContainerFacilityService,
    private yardService: ContainerTerminalYardService,
    private productsService: ProductsService
  ) {}

  ngOnInit(): void {
    this.route.data.subscribe(data => {
      this.master = (data['master'] || 'facilities') as SetupMaster;
      this.load();
    });
  }

  get title(): string {
    switch (this.master) {
      case 'areas': return 'Yard Areas';
      case 'slots': return 'Yard Slots';
      case 'charge-rules': return 'Yard Charge Rules';
      case 'execution-values': return 'Execution Values';
      default: return 'Terminal Facilities';
    }
  }

  get subtitle(): string {
    switch (this.master) {
      case 'areas': return 'Operational yard zones used by gate-in, moves, holds, and storage control.';
      case 'slots': return 'Block, row, bay, tier, slot capacity, and container compatibility master data.';
      case 'charge-rules': return 'Storage, demurrage, detention, lift-on, and lift-off charge rule setup.';
      case 'execution-values': return 'Read-only land execution values used by container tracking workflows.';
      default: return 'Shared facility master for terminals, bonded yards, CFS, depots, and carrier yards.';
    }
  }

  get addRoute(): string[] {
    return ['/container-tracking/setup', this.routeSegment, 'add'];
  }

  get canAdd(): boolean {
    return this.master !== 'execution-values';
  }

  get metrics(): PageMetric[] {
    if (this.master === 'execution-values') {
      return [
        { label: 'Transport Modes', value: this.transportModes.length, tone: 'info' },
        { label: 'Facility Types', value: this.storageFacilityTypes.length, tone: 'success' },
        { label: 'Area Types', value: this.areaTypes.length, tone: 'warn' },
        { label: 'Lifecycle Values', value: this.lifecycleMilestones.length, tone: 'info' }
      ];
    }
    const rows = this.currentRows();
    return [
      { label: 'Total', value: rows.length, tone: 'info' },
      { label: 'Active', value: rows.filter((row: any) => row.active !== false).length, tone: 'success' },
      { label: 'Inactive', value: rows.filter((row: any) => row.active === false).length, tone: 'default' },
      { label: 'Facilities', value: this.facilities.length, tone: 'info' }
    ];
  }

  get filteredFacilities(): ContainerFacility[] {
    return this.facilities.filter(facility =>
      this.matchesKeyword([facility.facilityCode, facility.facilityName, facility.facilityType, facility.operationMode, this.supplierName(facility.operatorCrmClientId), this.warehouseName(facility.linkedWarehouseId)])
      && this.matchesActive(facility.active)
    );
  }

  get filteredAreas(): ContainerYardArea[] {
    return this.areas.filter(area =>
      (!this.facilityFilter || area.facilityId === this.facilityFilter)
      && this.matchesActive(area.active)
      && this.matchesKeyword([area.areaCode, area.areaName, area.areaType, this.facilityName(area.facilityId)])
    );
  }

  get filteredSlots(): ContainerYardSlot[] {
    return this.slots.filter(slot =>
      (!this.facilityFilter || slot.facilityId === this.facilityFilter)
      && this.matchesActive(slot.active)
      && this.matchesKeyword([slot.slotCode, slot.blockCode, slot.rowCode, slot.bayCode, slot.tierCode, this.facilityName(slot.facilityId), this.areaName(slot.areaId)])
    );
  }

  get filteredChargeRules(): ContainerYardChargeRule[] {
    return this.chargeRules.filter(rule =>
      (!this.facilityFilter || rule.facilityId === this.facilityFilter)
      && this.matchesActive(rule.active)
      && this.matchesKeyword([rule.chargeType, rule.chargeBasis, rule.containerType, this.facilityName(rule.facilityId), this.areaName(rule.areaId)])
    );
  }

  get catalogRows(): SetupCatalogItem[] {
    return [...this.transportModes, ...this.storageFacilityTypes, ...this.areaTypes, ...this.legStatuses, ...this.lifecycleMilestones]
      .filter(row => this.matchesKeyword([row.code, row.label, row.category, row.description]));
  }

  load(): void {
    const companyId = this.companyId();
    if (!companyId) {
      return;
    }
    this.isLoading = true;
    this.facilityService.findAll(new HttpParams().set('owningCompanyId', String(companyId)).set('size', '500').set('sort', 'facilityName,asc')).subscribe({
      next: page => {
        this.facilities = page.content || [];
        this.isLoading = false;
      },
      error: () => {
        this.facilities = [];
        this.isLoading = false;
      }
    });
    this.yardService.findAreas(new HttpParams().set('companyId', String(companyId))).subscribe({ next: rows => this.areas = rows || [], error: () => this.areas = [] });
    this.yardService.findSlots(new HttpParams().set('companyId', String(companyId))).subscribe({ next: rows => this.slots = rows || [], error: () => this.slots = [] });
    this.yardService.findChargeRules(companyId).subscribe({ next: rows => this.chargeRules = rows || [], error: () => this.chargeRules = [] });
    this.productsService.getLogisticsCatalog(companyId, 'CONTAINER_YARD').subscribe({
      next: products => this.logisticsProducts = products || [],
      error: () => this.logisticsProducts = []
    });
    this.warehousingService.getMasterData('WAREHOUSE', { activeStatus: 'ACTIVE' }).subscribe({ next: response => this.warehouses = response.data || [], error: () => this.warehouses = [] });
    this.crmService.getClients(companyId, ['SUPPLIER', 'WAREHOUSE_SUPPLIER', 'TRANSPORTER']).subscribe({ next: rows => this.supplierClients = rows || [], error: () => this.supplierClients = [] });
  }

  clearFilters(): void {
    this.keyword = '';
    this.facilityFilter = null;
    this.activeFilter = 'ALL';
  }

  facilityName(id?: number | null): string {
    return this.facilities.find(item => item.id === id)?.facilityName || (id ? `Facility #${id}` : '-');
  }

  areaName(id?: number | null): string {
    return this.areas.find(item => item.id === id)?.areaName || this.areas.find(item => item.id === id)?.areaCode || (id ? `Area #${id}` : '-');
  }

  warehouseName(id?: number | null): string {
    return this.warehouses.find(item => item.id === id)?.name || (id ? `Warehouse #${id}` : '-');
  }

  supplierName(id?: number | null): string {
    return this.supplierClients.find(item => item.id === id)?.companyName || (id ? `Supplier #${id}` : '-');
  }

  typeLabel(value?: string | null): string {
    return (value || '-').toLowerCase().split('_').filter(Boolean).map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
  }

  productName(id?: number | null): string {
    return this.logisticsProducts.find(product => product.productID === id)?.name || 'Manual charge';
  }

  productCode(id?: number | null): string {
    return this.logisticsProducts.find(product => product.productID === id)?.productCode || '';
  }

  editRoute(id?: number): string[] {
    return ['/container-tracking/setup', this.routeSegment, String(id || 0), 'edit'];
  }

  private get routeSegment(): string {
    return this.master === 'slots' ? 'yard-slots' : this.master;
  }

  private currentRows(): any[] {
    switch (this.master) {
      case 'areas': return this.areas;
      case 'slots': return this.slots;
      case 'charge-rules': return this.chargeRules;
      default: return this.facilities;
    }
  }

  private matchesActive(active?: boolean): boolean {
    return this.activeFilter === 'ALL'
      || (this.activeFilter === 'ACTIVE' && active !== false)
      || (this.activeFilter === 'INACTIVE' && active === false);
  }

  private matchesKeyword(values: Array<string | number | null | undefined>): boolean {
    const keyword = this.keyword.trim().toLowerCase();
    return !keyword || values.some(value => String(value || '').toLowerCase().includes(keyword));
  }

  private companyId(): number {
    return this.sessionStorageService.getCompany()?.id || 0;
  }
}
